import { Box, Icon, Text } from '@factorialco/deprecated-design-system'

import PopoverMenu from 'modules/core/components/PopoverMenu'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { PayrollPhase, PayrollStatus } from 'modules/payroll_bundle/lib/enums'
import { usePayrollContext } from 'modules/payroll_bundle/lib/payrollContext'
import {
  PayrollPhaseDefineStatus,
  getPayrollPhase,
} from 'modules/payroll_bundle/store/models/PolicyPeriod'

import { CollabStatusDescription, copiesForStatus } from './utils'

interface CollaborateWidgetProps {
  readonly?: boolean
  status?: PayrollStatus
}

export default function CollaborateWidget({
  readonly,
  status: currentStatus,
}: CollaborateWidgetProps) {
  const { payrollDispatch } = usePayrollContext()
  const disabled =
    readonly ||
    !currentStatus ||
    getPayrollPhase(currentStatus) !== PayrollPhase.SUPPLEMENTS_DEFINITION

  const collaborationStatuses = useUnsafeMemo<CollabStatusDescription[]>(() => {
    const phase1Statuses = Object.values(PayrollStatus).filter(
      (status): status is PayrollPhaseDefineStatus =>
        getPayrollPhase(status) === PayrollPhase.SUPPLEMENTS_DEFINITION
    )

    return phase1Statuses.map((status) => {
      const { title, description, icon } = copiesForStatus(status)
      const isActive = status === currentStatus

      return {
        status,
        title,
        description,
        disabled: isActive,
        icon,
        action: () => {
          payrollDispatch({
            type: 'SHOW_CHANGE_STATUS_MODAL',
            payload: status,
          })
          track(trackingEvents.payroll.userClicksOnPayrollCollaboration, {
            period_status: status,
          })
        },
      }
    })
  }, [payrollDispatch, currentStatus])

  const activeCollaborationStatus = collaborationStatuses.find((s) => s.status === currentStatus)

  return (
    <Box>
      <PopoverMenu type="list">
        <PopoverMenu.Trigger
          aria-label={i18n.t(
            'payroll_bundle.policy_period.cycle_process.collaborate_widget.aria_label'
          )}
          disabled={disabled}
        >
          <Box
            paddingLeft="s12"
            paddingRight="s8"
            paddingY="s6"
            flexDirection="row"
            gap="s8"
            style={{
              opacity: disabled ? '0.3' : '1',
              cursor: disabled ? 'not-allowed' : 'pointer',
            }}
            borderRadius={{ all: 'abs008' }}
            border={{
              all: {
                width: 's2',
                style: 'solid',
                color: 'grey400',
              },
            }}
          >
            <Text.Normal weight="medium">
              {i18n.t('payroll_bundle.policy_period.cycle_process.collaborate_widget.label')}
            </Text.Normal>
            <Text.Normal>
              {activeCollaborationStatus?.title ??
                i18n.t('payroll_bundle.policy_period.cycle_process.collaborate_widget.disabled')}
            </Text.Normal>
            <Icon.Medium icon="chevron-down" />
          </Box>
        </PopoverMenu.Trigger>
        <PopoverMenu.Content side="bottom" align="start" width="s400">
          {collaborationStatuses.map(({ status, title, description, icon, disabled, action }) => (
            <PopoverMenu.TouchableOption
              icon={icon}
              key={status}
              onClick={action}
              title={title}
              disabled={disabled}
              description={description}
            />
          ))}
        </PopoverMenu.Content>
      </PopoverMenu>
    </Box>
  )
}
